import axios from "axios";
import {
  cachePresets, getCachedPresets, upsertLocal, removeLocal,
  replaceLocalId, enqueue, getQueue, popFront, queueSize, isLocalId,
  newLocalId, nowIso,
} from "./offlineStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 8000,
});

// Treat network-level failures + 5xx as offline (queue-worthy);
// 4xx bubbles up because the request is malformed regardless of network.
function isOfflineError(err) {
  if (!err) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  if (err.message === "Network Error") return true;
  const status = err.response?.status;
  if (typeof status === "number" && status >= 500) return true;
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  return false;
}

// ---- READS ---------------------------------------------------------------

export async function listPresets() {
  try {
    const { data } = await api.get("/presets");
    cachePresets(data);
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      return getCachedPresets();
    }
    throw err;
  }
}

// ---- WRITES --------------------------------------------------------------

export async function createPreset(payload) {
  const optimistic = {
    ...payload,
    id: newLocalId(),
    created_at: nowIso(),
    updated_at: nowIso(),
    _pending: "create",
  };
  upsertLocal(optimistic);

  try {
    const { data } = await api.post("/presets", payload);
    replaceLocalId(optimistic.id, data);
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      enqueue({ type: "create", localId: optimistic.id, payload });
      return optimistic;
    }
    // Non-offline failure: roll back the optimistic entry
    removeLocal(optimistic.id);
    throw err;
  }
}

export async function updatePreset(id, payload) {
  // Optimistic update in local store
  const list = getCachedPresets();
  const existing = list.find((p) => p.id === id);
  const merged = {
    ...(existing || { id }),
    ...payload,
    id,
    updated_at: nowIso(),
    _pending: existing?._pending || "update",
  };
  upsertLocal(merged);

  // If this row was created locally and hasn't synced yet, queue a merged create instead.
  if (isLocalId(id)) {
    enqueue({ type: "create", localId: id, payload: { ...merged } });
    return merged;
  }

  try {
    const { data } = await api.put(`/presets/${id}`, payload);
    upsertLocal(data);
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      enqueue({ type: "update", id, payload });
      return merged;
    }
    throw err;
  }
}

export async function deletePreset(id) {
  removeLocal(id);
  if (isLocalId(id)) {
    // Not on server; nothing to sync.
    return { success: true, id };
  }
  try {
    const { data } = await api.delete(`/presets/${id}`);
    return data;
  } catch (err) {
    if (isOfflineError(err)) {
      enqueue({ type: "delete", id });
      return { success: true, id, queued: true };
    }
    throw err;
  }
}

// ---- SYNC ----------------------------------------------------------------

let _syncing = false;
export async function syncQueue({ onProgress } = {}) {
  if (_syncing) return { synced: 0, skipped: true };
  _syncing = true;
  let synced = 0;
  try {
    while (getQueue().length > 0) {
      const op = getQueue()[0];
      try {
        if (op.type === "create") {
          const { data } = await api.post("/presets", op.payload);
          replaceLocalId(op.localId, data);
        } else if (op.type === "update") {
          const { data } = await api.put(`/presets/${op.id}`, op.payload);
          upsertLocal(data);
        } else if (op.type === "delete") {
          await api.delete(`/presets/${op.id}`);
        }
        popFront();
        synced += 1;
        onProgress?.({ remaining: queueSize(), synced });
      } catch (err) {
        if (isOfflineError(err)) break; // still offline, bail
        // Non-recoverable: drop the op so we don't loop forever
        popFront();
      }
    }
  } finally {
    _syncing = false;
  }
  // After sync, refresh the local mirror from server (best effort)
  try {
    const { data } = await api.get("/presets");
    cachePresets(data);
    return { synced, presets: data };
  } catch {
    return { synced, presets: getCachedPresets() };
  }
}

export function pendingSyncCount() {
  return queueSize();
}
